import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
} from '@angular/core';
import { ExportFormat } from '../../../../../shared/types/export-format.types';
import {
    getDisplayVolumeDiscountTiers,
    inferAppliedVolumeDiscount,
    VolumeDiscountTier,
} from '../../../../../shared/utils/volume-discount.utils';

export interface SupplierCardInfo {
    mov: number;
    id?: string;
    code?: string;
    promotionTextColor?: string;
    promotionDescription?: string;
    supplyType?: string;
    leadTimeFrom?: number;
    leadTimeTo?: number;
    prePaymentPercentage?: number;
    fullPackageOnly?: boolean;
    preOrderShippingDate?: string;
    volumeDiscounts?: unknown;
}

export interface SupplierTierChip {
    key: string;
    label: string;
    minCents: number;
    active: boolean;
}

@Component({
    selector: 'vsf-product-list-supplier-card',
    templateUrl: './product-list-supplier-card.component.html',
    styleUrls: ['./product-list-supplier-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListSupplierCardComponent {
    @Input() supplierInfo: SupplierCardInfo | null = null;
    @Input() supplierSpecificTotal = 0;
    @Input() isPreOrder = false;
    @Input() progressPercentage = 0;
    @Input() progressColor = '#0d182d';
    @Input() statusText = '';
    @Input() deliveryTerm: string | null = null;
    @Input() showPrePayment = false;
    @Input() pricelistCardText: Record<string, string> = {};
    @Input() selectedTierMinCents: number | null = null;

    @Output() downloadSupplierProductsEvent = new EventEmitter<ExportFormat>();
    @Output() shareSupplierLinkEvent = new EventEmitter<ExportFormat>();
    @Output() selectTierEvent = new EventEmitter<number>();

    get volumeDiscountTiers(): VolumeDiscountTier[] {
        return getDisplayVolumeDiscountTiers(this.supplierInfo?.volumeDiscounts);
    }

    get appliedVolumeDiscountPercent(): number | null {
        const applied = inferAppliedVolumeDiscount(
            this.supplierSpecificTotal,
            this.supplierInfo?.volumeDiscounts
        );
        return applied?.discountPercent ?? null;
    }

    get remainingCents(): number {
        return Math.max((Number(this.supplierInfo?.mov) || 0) - (this.supplierSpecificTotal || 0), 0);
    }

    get tierChips(): SupplierTierChip[] {
        const mov = Number(this.supplierInfo?.mov) || 0;
        const chips: SupplierTierChip[] = [];
        if (mov > 0) {
            chips.push({
                key: 'mov',
                label: `${this.formatEuro(mov)} (MOV)`,
                minCents: mov,
                active: false,
            });
        }
        for (const tier of this.volumeDiscountTiers) {
            const minCents = Math.round(tier.orderValueMin * 100);
            if (mov > 0 && minCents === mov) continue;
            chips.push({
                key: `tier-${tier.orderValueMin}`,
                label: `${this.formatEuro(minCents)}+`,
                minCents,
                active: false,
            });
        }
        if (!chips.length) return chips;

        const selected =
            this.selectedTierMinCents != null
                ? this.selectedTierMinCents
                : this.resolveActiveTierMin(chips);
        return chips.map((chip) => ({
            ...chip,
            active: chip.minCents === selected,
        }));
    }

    selectTier(chip: SupplierTierChip): void {
        this.selectTierEvent.emit(chip.minCents);
    }

    private resolveActiveTierMin(chips: SupplierTierChip[]): number {
        const total = this.supplierSpecificTotal || 0;
        let current = chips[0].minCents;
        for (const chip of chips) {
            if (total >= chip.minCents) current = chip.minCents;
        }
        return current;
    }

    private formatEuro(cents: number): string {
        const value = Math.round(cents) / 100;
        return `€${value.toLocaleString('en-GB', {
            maximumFractionDigits: value % 1 === 0 ? 0 : 2,
        })}`;
    }
}
